import { threatMonitor } from '@services/security/threatMonitor';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  sanitizedValue?: string;
  threatLevel: 'none' | 'low' | 'medium' | 'high';
  detectedThreats: string[];
}

export interface ValidationOptions {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  allowHTML?: boolean;
  allowScripts?: boolean;
  customPatterns?: RegExp[];
  stripWhitespace?: boolean;
}

class InputValidator {
  private xssPatterns: RegExp[] = [
    /<script[^>]*>.*?<\/script>/gi,
    /<iframe[^>]*>.*?<\/iframe>/gi,
    /<object[^>]*>.*?<\/object>/gi,
    /<embed[^>]*>/gi,
    /<link[^>]*>/gi,
    /<meta[^>]*>/gi,
    /javascript:/gi,
    /vbscript:/gi,
    /onload=/gi,
    /onerror=/gi,
    /onclick=/gi,
    /onmouseover=/gi,
    /onfocus=/gi,
    /onblur=/gi,
    /onchange=/gi,
    /onsubmit=/gi,
    /onkeydown=/gi,
    /onkeyup=/gi,
    /onkeypress=/gi,
    /onmousedown=/gi,
    /onmouseup=/gi,
    /onmousemove=/gi,
    /onmouseout=/gi,
    /oncontextmenu=/gi,
    /ondblclick=/gi,
    /ondrag=/gi,
    /ondrop=/gi,
    /onresize=/gi,
    /onscroll=/gi,
    /onselect=/gi,
    /onunload=/gi,
    /eval\s*\(/gi,
    /expression\s*\(/gi,
    /\.innerHTML/gi,
    /\.outerHTML/gi,
    /document\.write/gi,
    /document\.writeln/gi
  ];

  private sqlInjectionPatterns: RegExp[] = [
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/gi,
    /(\b(OR|AND)\b\s+\b\d+\b\s*=\s*\b\d+\b)/gi,
    /(\b(OR|AND)\b\s+\b\w+\b\s*=\s*\b\w+\b)/gi,
    /('|(\\')|(;)|(\*)|(@)|(char)|(nchar)|(varchar)|(nvarchar)|(alter)|(begin)|(cast)|(create)|(cursor)|(declare)|(delete)|(drop)|(end)|(exec)|(execute)|(fetch)|(insert)|(kill)|(select)|(sys)|(sysobjects)|(syscolumns)|(table)|(update))/gi,
    /((%27)|(\')|((%3D)|(=))[^\n]*((%27)|(\')|((%2D)|(-))))/gi,
    /((%27)|(\'))union/gi,
    /exec(\s|\+)+(s|x)p\w+/gi
  ];

  private pathTraversalPatterns: RegExp[] = [
    /\.\.\//gi,
    /\.\.\\/gi,
    /%2e%2e%2f/gi,
    /%2e%2e%5c/gi,
    /%252e%252e%252f/gi,
    /\.\./gi,
    /%c0%ae%c0%ae%c0%af/gi,
    /%c1%9c/gi
  ];

  private commandInjectionPatterns: RegExp[] = [
    /;\s*(ls|cat|echo|pwd|whoami|id|uname|ps|kill|rm|mv|cp|chmod|chown|su|sudo|wget|curl|nc|netcat|telnet|ssh|ftp|tftp|scp|rsync)/gi,
    /\|\s*(ls|cat|echo|pwd|whoami|id|uname|ps|kill|rm|mv|cp|chmod|chown|su|sudo|wget|curl|nc|netcat|telnet|ssh|ftp|tftp|scp|rsync)/gi,
    /&&\s*(ls|cat|echo|pwd|whoami|id|uname|ps|kill|rm|mv|cp|chmod|chown|su|sudo|wget|curl|nc|netcat|telnet|ssh|ftp|tftp|scp|rsync)/gi,
    /`[^`]*`/gi,
    /\$\([^)]*\)/gi
  ];

  /**
   * Validate input against security threats
   */
  validateInput(input: string, options: ValidationOptions = {}): ValidationResult {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      threatLevel: 'none',
      detectedThreats: []
    };

    // Basic validation
    if (options.required && (!input || input.trim().length === 0)) {
      result.isValid = false;
      result.errors.push('Input is required');
      return result;
    }

    if (!input) {
      result.sanitizedValue = '';
      return result;
    }

    // Length validation
    if (options.minLength && input.length < options.minLength) {
      result.isValid = false;
      result.errors.push(`Input must be at least ${options.minLength} characters long`);
    }

    if (options.maxLength && input.length > options.maxLength) {
      result.isValid = false;
      result.errors.push(`Input must not exceed ${options.maxLength} characters`);
    }

    // Threat detection
    this.detectThreats(input, result);

    // Sanitization
    result.sanitizedValue = this.sanitizeInput(input, options);

    // Log security events for detected threats
    if (result.detectedThreats.length > 0) {
      const severity = this.getThreatSeverity(result.threatLevel);
      threatMonitor.logEvent({
        type: 'input_validation',
        severity,
        source: 'input_validator',
        details: {
          input: input.substring(0, 100), // Limit logged input length
          threats: result.detectedThreats,
          threatLevel: result.threatLevel,
          sanitized: result.sanitizedValue?.substring(0, 100)
        }
      });
    }

    return result;
  }

  /**
   * Validate email format
   */
  validateEmail(email: string): ValidationResult {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const result = this.validateInput(email, { maxLength: 254 });
    
    if (result.isValid && !emailRegex.test(email)) {
      result.isValid = false;
      result.errors.push('Invalid email format');
    }

    return result;
  }

  /**
   * Validate URL format
   */
  validateURL(url: string): ValidationResult {
    const result = this.validateInput(url, { maxLength: 2048 });
    
    if (result.isValid) {
      try {
        const parsed = new URL(url);
        if (!['http:', 'https:'].includes(parsed.protocol)) {
          result.isValid = false;
          result.errors.push('URL must use HTTP or HTTPS protocol');
        }
      } catch {
        result.isValid = false;
        result.errors.push('Invalid URL format');
      }
    }

    return result;
  }

  /**
   * Validate password strength
   */
  validatePassword(password: string): ValidationResult {
    const result = this.validateInput(password, { 
      required: true, 
      minLength: 8, 
      maxLength: 128 
    });

    if (result.isValid) {
      const hasLower = /[a-z]/.test(password);
      const hasUpper = /[A-Z]/.test(password);
      const hasDigit = /\d/.test(password);
      const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

      if (!hasLower) result.errors.push('Password must contain lowercase letters');
      if (!hasUpper) result.errors.push('Password must contain uppercase letters');
      if (!hasDigit) result.errors.push('Password must contain numbers');
      if (!hasSpecial) result.errors.push('Password must contain special characters');

      if (result.errors.length > 0) {
        result.isValid = false;
      }
    }

    return result;
  }

  /**
   * Validate JSON input
   */
  validateJSON(jsonString: string): ValidationResult & { parsedJSON?: unknown } {
    const result = this.validateInput(jsonString, { maxLength: 10000 });
    
    if (result.isValid) {
      try {
        const parsed = JSON.parse(jsonString);
        return { ...result, parsedJSON: parsed };
      } catch {
        result.isValid = false;
        result.errors.push('Invalid JSON format');
      }
    }

    return result;
  }

  /**
   * Sanitize HTML content
   */
  sanitizeHTML(html: string): string {
    if (!html) return '';

    // Remove dangerous tags and attributes
    let sanitized = html;
    
    // Remove script tags and content
    sanitized = sanitized.replace(/<script[^>]*>.*?<\/script>/gi, '');
    
    // Remove dangerous tags
    sanitized = sanitized.replace(/<(iframe|object|embed|link|meta|style)[^>]*>.*?<\/\1>/gi, '');
    sanitized = sanitized.replace(/<(iframe|object|embed|link|meta|input|form|button)[^>]*>/gi, '');
    
    // Remove event handlers
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^>\s]+/gi, '');
    
    // Remove javascript: and vbscript: protocols
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    
    return sanitized;
  }

  /**
   * Detect security threats in input
   */
  private detectThreats(input: string, result: ValidationResult): void {
    let maxThreatLevel = 0;

    // Check for XSS patterns
    for (const pattern of this.xssPatterns) {
      if (pattern.test(input)) {
        result.detectedThreats.push('XSS');
        maxThreatLevel = Math.max(maxThreatLevel, 3);
        break;
      }
    }

    // Check for SQL injection patterns
    for (const pattern of this.sqlInjectionPatterns) {
      if (pattern.test(input)) {
        result.detectedThreats.push('SQL Injection');
        maxThreatLevel = Math.max(maxThreatLevel, 3);
        break;
      }
    }

    // Check for path traversal patterns
    for (const pattern of this.pathTraversalPatterns) {
      if (pattern.test(input)) {
        result.detectedThreats.push('Path Traversal');
        maxThreatLevel = Math.max(maxThreatLevel, 2);
        break;
      }
    }

    // Check for command injection patterns
    for (const pattern of this.commandInjectionPatterns) {
      if (pattern.test(input)) {
        result.detectedThreats.push('Command Injection');
        maxThreatLevel = Math.max(maxThreatLevel, 3);
        break;
      }
    }

    // Set threat level
    switch (maxThreatLevel) {
      case 1:
        result.threatLevel = 'low';
        break;
      case 2:
        result.threatLevel = 'medium';
        break;
      case 3:
        result.threatLevel = 'high';
        break;
      default:
        result.threatLevel = 'none';
    }

    // Mark as invalid if high threat level
    if (maxThreatLevel >= 3) {
      result.isValid = false;
      result.errors.push('Input contains potentially malicious content');
    }
  }

  /**
   * Sanitize input based on options
   */
  private sanitizeInput(input: string, options: ValidationOptions): string {
    let sanitized = input;

    // Strip whitespace if requested
    if (options.stripWhitespace) {
      sanitized = sanitized.trim();
    }

    // Handle HTML content
    if (!options.allowHTML) {
      sanitized = this.sanitizeHTML(sanitized);
    }

    // Remove scripts if not allowed
    if (!options.allowScripts) {
      sanitized = sanitized.replace(/<script[^>]*>.*?<\/script>/gi, '');
      sanitized = sanitized.replace(/javascript:/gi, '');
      sanitized = sanitized.replace(/vbscript:/gi, '');
    }

    return sanitized;
  }

  /**
   * Get threat severity for logging
   */
  private getThreatSeverity(threatLevel: ValidationResult['threatLevel']): 'low' | 'medium' | 'high' | 'critical' {
    switch (threatLevel) {
      case 'high':
        return 'critical';
      case 'medium':
        return 'high';
      case 'low':
        return 'medium';
      default:
        return 'low';
    }
  }
}

// Export singleton instance
export const inputValidator = new InputValidator();
export default inputValidator;